import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2, Loader2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { translations } from "../i18n/translations";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";

const CONTACT_SERVICE_OPTIONS = [
  { value: "strategic-consulting", tr: "Stratejik Danışmanlık & Altyapı Analizi", en: "Strategic Consulting & Infrastructure Analysis" },
  { value: "cyber-security", tr: "Siber Güvenlik & FortiGate", en: "Cyber Security & FortiGate" },
  { value: "m365-intune", tr: "Microsoft 365 & Intune", en: "Microsoft 365 & Intune" },
  { value: "power-bi", tr: "Power BI & İş Zekası", en: "Power BI & Business Intelligence" },
  { value: "universal-print", tr: "Universal Print & ESG", en: "Universal Print & ESG" },
  { value: "cloud-migration", tr: "Bulut Migrasyonu (Azure/AWS)", en: "Cloud Migration (Azure/AWS)" },
  { value: "synology-nas", tr: "Synology NAS & Yedekleme", en: "Synology NAS & Backup" },
  { value: "bpm", tr: "BPM & Süreç Dijitalleşmesi", en: "BPM & Process Digitalization" },
  { value: "it-support", tr: "7/24 IT Destek", en: "24/7 IT Support" },
  { value: "other", tr: "Diğer", en: "Other" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { lang } = useLanguage();
  const t = translations[lang].contactPage;

  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setErrors((er) => ({ ...er, [e.target.name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t.required;
    if (!form.email.trim()) e.email = t.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t.invalidEmail;
    if (!form.message.trim()) e.message = t.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      const serviceLabel = CONTACT_SERVICE_OPTIONS.find((s) => s.value === form.service)?.[lang] || "";
      await axios.post(`${API}/contact`, {
        ...form,
        service_label: serviceLabel,
        language: lang,
      });
      setStatus("success");
      setForm({ name: "", company: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  const infoItems = [
    { icon: Phone, label: t.infoTitle, value: "0543 804 70 05", href: "tel:05438047005" },
    { icon: Mail, label: "E-posta", value: "info@adalcrea.com.tr", href: "mailto:info@adalcrea.com.tr" },
    { icon: MapPin, label: lang === "tr" ? "Adres" : "Address", value: t.address },
    { icon: Clock, label: t.hours, value: t.hoursValue },
  ];

  const inputBase =
    "w-full rounded-lg border bg-white px-4 py-3 text-navy-900 placeholder:text-slate-400 outline-none transition-colors focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

  return (
    <>
      <Seo title={t.hero} description={t.heroDesc} path="/iletisim" lang={lang} />
      <PageHero title={t.hero} desc={t.heroDesc} />

      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-10 lg:gap-14">
          {/* Info */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="font-display font-bold text-2xl text-navy-900">{t.infoTitle}</h2>
              <div className="mt-6 space-y-5">
                {infoItems.map((it, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
                      <it.icon className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                        {it.label}
                      </div>
                      {it.href ? (
                        <a href={it.href} className="text-navy-900 font-medium hover:text-amber-600 transition-colors">
                          {it.value}
                        </a>
                      ) : (
                        <div className="text-navy-900 font-medium">{it.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-slate-200 h-64">
              <iframe
                title="AdalCrea Konum"
                src="https://www.google.com/maps?q=Zuhuratbaba%20Mah.%20Bak%C4%B1rk%C3%B6y%20%C4%B0stanbul&output=embed"
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="rounded-3xl border border-slate-200 p-8 lg:p-10 shadow-sm">
              <h2 className="font-display font-bold text-2xl text-navy-900">{t.formTitle}</h2>

              {status === "success" ? (
                <div className="mt-8 flex flex-col items-center text-center py-10">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="w-9 h-9 text-emerald-600" />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-xl text-navy-900">
                    {t.successTitle}
                  </h3>
                  <p className="mt-2 text-slate-600 max-w-md">{t.successMsg}</p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="mt-6 text-amber-600 font-semibold hover:text-amber-700"
                  >
                    {t.formTitle}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="mt-6 space-y-5" noValidate>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-navy-900 mb-1.5">
                        {t.name} *
                      </label>
                      <input
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        className={`${inputBase} ${errors.name ? "border-red-400" : "border-slate-300"}`}
                      />
                      {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy-900 mb-1.5">
                        {t.company}
                      </label>
                      <input
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        className={`${inputBase} border-slate-300`}
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-navy-900 mb-1.5">
                        {t.email} *
                      </label>
                      <input
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        className={`${inputBase} ${errors.email ? "border-red-400" : "border-slate-300"}`}
                      />
                      {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-navy-900 mb-1.5">
                        {t.phone}
                      </label>
                      <input
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        className={`${inputBase} border-slate-300`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy-900 mb-1.5">
                      {t.service}
                    </label>
                    <select
                      name="service"
                      value={form.service}
                      onChange={handleChange}
                      className={`${inputBase} border-slate-300 appearance-none`}
                    >
                      <option value="">{t.selectService}</option>
                      {CONTACT_SERVICE_OPTIONS.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s[lang]}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-navy-900 mb-1.5">
                      {t.message} *
                    </label>
                    <textarea
                      name="message"
                      rows={5}
                      value={form.message}
                      onChange={handleChange}
                      placeholder={t.messagePlaceholder}
                      className={`${inputBase} resize-none ${errors.message ? "border-red-400" : "border-slate-300"}`}
                    />
                    {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message}</p>}
                  </div>

                  {status === "error" && (
                    <p className="text-sm text-red-500">{t.errorMsg}</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-70 text-navy-950 font-semibold px-6 py-3.5 rounded-lg transition-colors shadow-lg shadow-amber-500/20"
                  >
                    {status === "loading" ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {t.submitting}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {t.submit}
                      </>
                    )}
                  </button>

                  <p className="text-xs text-slate-400 text-center leading-relaxed pt-1" data-testid="contact-disclaimer">
                    {t.disclaimer}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
